import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './SearchDropdown.css';
import { getImageUrl } from '../../utils/imageUtils';
import { getAvailabilityLabel, isPurchasable } from '../../utils/availability';

const SearchDropdown = ({ onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState('');
  const searchInputRef = useRef(null);
  const navigate = useNavigate();

  
  const baseURL = window.APP_CONFIG?.API_URL || "https://backendweb.properteck.lk";

  // Focus input on mount
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${baseURL}/api/categories/getcategories`);
        let categoryData = response.data.categories || response.data || [];
        
        if (!Array.isArray(categoryData)) {
          categoryData = [];
        }
        
        const formattedCategories = categoryData.map(category => ({
          name: category.name || category.categoryName || category.title,
          value: category.value || category.slug || category.name?.toLowerCase() || category._id
        }));
        
        setCategories(formattedCategories);
      } catch (error) {
        console.error('Error fetching categories:', error);
        setCategories([]);
      }
    };

    fetchCategories();
  }, []);

  // Search products with debounce
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredProducts([]);
      setIsInitialLoad(true);
      return;
    }

    setIsLoading(true);
    setError('');
    
    const timeoutId = setTimeout(async () => {
      try {
        const response = await axios.get(`${baseURL}/api/products/getproducts`);
        const allProducts = response.data.products || response.data || [];
        
        if (!Array.isArray(allProducts)) {
          throw new Error('Invalid products data');
        }

        // Filter products based on search term
        const filtered = allProducts.filter(product =>
          product?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product?.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product?.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product?.type?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        
        setFilteredProducts(filtered.slice(0, 8)); // Limit to 8 results for dropdown
        setIsInitialLoad(false);
      } catch (error) {
        console.error('Error searching products:', error);
        setError('Failed to search products');
        setFilteredProducts([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Import the utility function at the top of the file
  // const getImageUrl = (imageUrl) => {
  //   if (!imageUrl) return 'http://propertecknew-production.up.railway.app/images/no-image.svg';
  //   return `http://propertecknew-production.up.railway.app/images/${imageUrl}`;
  // };

  const getCategoryName = (categoryValue) => {
    const category = categories.find(cat => cat.value === categoryValue);
    return category ? category.name : categoryValue;
  };

  const getProductTypeName = (typeValue) => {
    const typeMap = {
      'normal': 'Normal Product',
      'special_offer': 'Special Offer',
      'new_arrival': 'New Arrival'
    };
    return typeMap[typeValue] || typeValue;
  };

  const renderProductPrice = (product) => {
    if (product.type === 'special_offer') {
      return (
        <div className="custom-price-container">
          <span className="custom-offer-price">Rs.{product.offerPrice}</span>
          <span className="custom-regular-price">Rs.{product.regularPrice}</span>
          <span className="custom-discount-badge">{product.discount}% OFF</span>
        </div>
      );
    } else if (product.type === 'new_arrival') {
      return (
        <div className="custom-price-container">
          <span className="custom-offer-price">Rs.{product.offerPrice}</span>
          <span className="custom-regular-price">Rs.{product.regularPrice}</span>
        </div>
      );
    } else {
      return <span className="custom-normal-price">Rs.{product.price}</span>;
    }
  };

  const handleProductClick = (product) => {
    // Navigate to different URLs based on product type
    switch (product.type) {
      case 'special_offer':
        navigate(`/offer/${product._id}`);
        break;
      case 'new_arrival':
        navigate(`/newarrivals/${product._id}`);
        break;
      case 'normal':
      default:
        navigate(`/item/${product._id}`);
        break;
    }
    onClose();
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/search?query=${encodeURIComponent(searchTerm)}`);
      onClose();
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setFilteredProducts([]);
    setIsInitialLoad(true);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const toggleCompareProduct = (product, e) => {
    e.stopPropagation(); // Prevent product click
    const exists = selectedProducts.find(p => p._id === product._id);
    if (exists) {
      setSelectedProducts(prev => prev.filter(p => p._id !== product._id));
    } else {
      if (selectedProducts.length < 2) {
        setSelectedProducts(prev => [...prev, product]);
      } else {
        alert('You can only compare two products at a time.');
      }
    }
  };

  const handleCompareProducts = () => {
    // Navigate with state containing the selected products data
    navigate('/productComparison', {
      state: {
        products: selectedProducts
      }
    });
    onClose();
  };

  const handleCategorySearch = (categoryValue) => {
    navigate(`/${encodeURIComponent(categoryValue)}`);
    onClose();
  };

  const handleTypeSearch = (type) => {
    navigate(`/products?type=${encodeURIComponent(type)}`);
    onClose();
  };

  const handleViewAllResults = () => {
    navigate(`/search?query=${encodeURIComponent(searchTerm)}`);
    onClose();
  };

  return (
    <div className="custom-search-dropdown">
      <div className="custom-search-header">
        <form onSubmit={handleSearchSubmit} className="custom-search-form">
          <div className="custom-search-input-container">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search products, brands, categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="custom-search-input"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="custom-clear-search-btn"
                title="Clear search"
              >
                ×
              </button>
            )}
          </div>
          <button type="submit" className="custom-search-submit-btn">
            <i className="fas fa-search"></i>
            Search
          </button>
        </form>
      </div>

      <div className="custom-search-results">
        {isLoading && (
          <div className="custom-search-loading">
            <div className="custom-loading-spinner"></div>
            <span>Searching products...</span>
          </div>
        )}

        {error && (
          <div className="custom-search-error">
            <i className="fas fa-exclamation-triangle"></i>
            <p>{error}</p>
          </div>
        )}

        {!isLoading && !error && searchTerm && filteredProducts.length === 0 && !isInitialLoad && (
          <div className="custom-no-results">
            <i className="fas fa-search"></i>
            <p>No products found for "{searchTerm}"</p>
            <small>Try different keywords or browse categories below</small>
          </div>
        )}

        {!isLoading && !error && filteredProducts.length > 0 && (
          <div className="custom-products-list">
            <div className="custom-results-header">
              <span>Found {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}</span>
              {selectedProducts.length > 0 && (
                <span className="custom-compare-count">
                  {selectedProducts.length}/2 selected for comparison
                </span>
              )}
            </div>

            {filteredProducts.map((product) => (
              <div key={product._id} className="custom-product-item">
                <input
                  type="checkbox"
                  className="custom-compare-checkbox"
                  checked={selectedProducts.some(p => p._id === product._id)}
                  onChange={(e) => toggleCompareProduct(product, e)}
                  title="Select for comparison"
                />
                
                <div className="custom-product-info-wrapper" onClick={() => handleProductClick(product)}>
                  <div className="custom-product-image">
                    <img 
                      src={getImageUrl(product.imageUrl)} 
                      alt={product.name}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = getImageUrl(null);
                      }}
                    />
                    {product.type === 'special_offer' && (
                      <div className="custom-product-badge custom-special-offer">
                        <i className="fas fa-fire"></i>
                      </div>
                    )}
                    {product.type === 'new_arrival' && (
                      <div className="custom-product-badge custom-new-arrival">
                        <i className="fas fa-star"></i>
                      </div>
                    )}
                  </div>
                  
                  <div className="custom-product-info">
                    <h4 className="custom-product-name">{product.name}</h4>
                    <div className="custom-product-meta">
                      <span className="custom-product-category">{getCategoryName(product.category)}</span>
                      <span className={`custom-product-type ${product.type}`}>
                        {getProductTypeName(product.type)}
                      </span>
                    </div>
                    <div className="custom-product-pricing">
                      {renderProductPrice(product)}
                    </div>
                    <div className="custom-product-stock">
                      <i className="fas fa-cube"></i>
                      <span className={!isPurchasable(product) ? 'custom-low-stock' : ''}>
                        {getAvailabilityLabel(product)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {selectedProducts.length === 2 && (
              <div className="custom-compare-button-wrapper">
                <button
                  className="custom-compare-btn"
                  onClick={handleCompareProducts}
                >
                  <i className="fas fa-balance-scale"></i>
                  Compare Selected Products
                </button>
              </div>
            )}

            {filteredProducts.length >= 8 && (
              <div className="custom-view-all-results">
                <button className="custom-view-all-btn" onClick={handleViewAllResults}>
                  View All Results ({searchTerm})
                </button>
              </div>
            )}
          </div>
        )}

        {!searchTerm && (
          <div className="custom-search-suggestions">
            <div className="custom-suggestion-section">
              <h4><i className="fas fa-fire"></i> Quick Searches</h4>
              <div className="custom-suggestions-list">
                <button onClick={() => setSearchTerm('laptop')}>Laptops</button>
                <button onClick={() => setSearchTerm('keyboard')}>Keyboards</button>
                <button onClick={() => setSearchTerm('mouse')}>Gaming Mouse</button>
                <button onClick={() => setSearchTerm('monitor')}>Monitors</button>
                <button onClick={() => setSearchTerm('gpu')}>Graphics Cards</button>
              </div>
            </div>

            {categories.length > 0 && (
              <div className="custom-suggestion-section">
                <h4><i className="fas fa-folder"></i> Browse Categories</h4>
                <div className="custom-suggestions-list">
                  {categories.slice(0, 6).map(category => (
                    <button 
                      key={category.value} 
                      onClick={() => handleCategorySearch(category.value)}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchDropdown;